/* 通用方法  */
import moment from 'moment';
import { cloneDeep } from 'lodash';
import { defaultTableOrder } from './const.js';
import { baseAuth } from '~/common/utils/json.js';
import { storage } from '~/common/utils/storage.js';
import { deepAssign } from '~/common/utils/index.js';
import { icFilters, getIn } from '~/common/plugins/ic-utils.js';
const { formatNumber } = icFilters;

/* 【将字符串大写】
 ** 参数说明：
 ** value: String, // 字段值[必须]
 ** none: '--', // 当字段值不存在时显示的默认值[非必须]
 */
export const changeToUpperCase = (value = '', none = '--') => {
  if (value === '' || value === null || typeof value === 'undefined') return none;
  return value.toString().toUpperCase();
};

/* 【表格字段为空判断并处理】
 ** 使用说明：主要是用来判断值是否存在，不存在则展示一个默认值
 ** 参数说明：
 ** value: '订单域', // 字段值[必须]
 ** showValue: '--', // 当字段值不存在时显示的默认值[非必须]
 */
export const checkEmptyValue = (value, showValue = '--') => {
  if (typeof value === 'undefined') return showValue;
  if (typeof value === 'number') return isNaN(value) ? showValue : value;
  if (!value) return showValue;
  return Object.keys(value).length > 0 ? value : showValue;
};

/* 【去除对象中值为空的字段】
 ** 使用说明：主要是用来去掉对象中值为null、undefined、''的属性，最终返回一个新的对象
 ** 参数说明：
 ** obj: {xxx} // 参数对象[必须]
 */
export const clearEmptyProp = obj => {
  const cloneObj = cloneDeep(obj);
  Object.keys(cloneObj).forEach(item => {
    if (cloneObj[item] === null || cloneObj[item] === undefined || cloneObj[item] === '') {
      delete cloneObj[item];
    }
  });
  return cloneObj;
};

/* 【表格筛选条件的对象处理】
 ** 使用说明：
 ** 1.去掉对象中值为null、undefined、''的属性
 ** 2.将时间范围字段构建出新的属性和值，最终返回一个新的对象
 ** 参数说明：
 ** paramObj: {xxx}, // 原参数对象[必须]
 ** arrParamName: [{xxx}], // 时间范围字段需要生成的新字段[非必须]
 */
export const formatSearchParam = (paramObj = {}, arrParamName = []) => {
  // 拷贝参数对象
  let cloneParam = cloneDeep(paramObj);
  if (!Array.isArray(arrParamName) || arrParamName.length === 0) {
    return clearEmptyProp(cloneParam);
  }
  // 对时间范围值进行处理
  arrParamName.forEach(item => {
    const inNameParam = cloneParam[item.inName] || [];
    if (inNameParam.length > 1) {
      // 如果是一个数组并且有2个值，增加两个参数并赋值
      cloneParam[item.outName[0]] = new Date(inNameParam[0]).getTime();
      cloneParam[item.outName[1]] = new Date(inNameParam[1]).getTime();
    } else {
      // 否则把这个参数连同增加的两个参数一起删掉
      delete cloneParam[item.inName];
      delete cloneParam[item.outName[0]];
      delete cloneParam[item.outName[1]];
    }
  });
  // 最后返回出去
  return clearEmptyProp(cloneParam);
};

/* 【表格表头排序值】
 ** 使用说明：用于生成一个拼接的字符串返回出去，作为排序参数
 ** 参数说明：
 ** prop: 'updated_at', // 排序的字段[必须]
 ** order: 'descending', // 排序方式[非必须]。'descending'=逆序，'ascending'='正序'
 ** defaultOrder: 'updated_at descending', // prop或者order不传时的默认排序，按更新时间逆序[非必须]
 */
export const formatTableOrder = (prop = 'updated_at', order = 'descending', defaultOrder = defaultTableOrder) => {
  if (!order) {
    return defaultOrder;
  }
  return `${prop} ${order}`;
};

/* 【数字显示处理，可带后缀】
 ** 使用说明：用于判断是否是数字或者字符串数字，是就做成进三位加一个逗号，不是就显示none配置的值，并且可以拼接后缀
 ** 参数说明：
 ** value: 50, // 数值[必须]
 ** suffix: '%', // 后缀[非必须]
 ** none: '--' // 无值时显示什么[非必须，默认'--']
 */
export const formatShowNumber = (value, suffix = '', empty = '--') => {
  const changeNumber = parseFloat(value);
  return isNaN(changeNumber) ? empty : `${formatNumber(changeNumber)}${suffix}`;
};

/* 【数字显示处理，强制加小数位】
 ** 使用说明：处理成隔三一个逗号、强制后面小数位，小数位可配置
 ** 参数说明：
 ** value: 50, // 数值[必须]
 ** float: 4, // 几位小数[非必须，默认4位]
 ** none: '0.00', // 无值时默认显示什么[非必须]
 */
export const formatFloatNumber = (value, float = 4, none = '--') => {
  const changeNumber = parseFloat(value);
  if (isNaN(changeNumber)) return none;
  const floatNumber = changeNumber.toFixed(float);
  const arrSplit = floatNumber.split('.');
  return `${Number(arrSplit[0]).toLocaleString('en-US')}.${arrSplit[1]}`;
};

/* 【数字显示处理，可带前缀】
 ** 使用说明：用于判断是否是数字或者字符串数字，是就做成进三位加一个逗号，不是就显示none配置的值，并且可以拼接前缀
 ** 参数说明：
 ** value: 50, // 数值[必须]
 ** prefix: '￥', // 前缀[非必须]
 ** none: '--' // 无值时显示什么[非必须，默认'--']
 */
export const formatNumberPrefix = (value, prefix = '', none = '--') => {
  const changeNumber = parseFloat(value);
  return isNaN(changeNumber) ? none : `${prefix}${formatNumber(changeNumber)}`;
};

/* 【将数值调整为大于等于0的数】
 ** 使用说明：强制矫正为大于等于0的数
 ** 参数说明：
 ** value: 50, // 数值[必须]
 ** none: 0 // 不符合条件时返回什么[非必须，默认0]
 */
export const setPositiveNumber = (value, none = 0) => {
  const toNum = Number(value);
  if (isNaN(toNum) || toNum <= 0) return none;
  return toNum;
};

/* 【将值转成4位小数】
 ** 使用说明：无值时为0，有值时设为4位小数
 ** 参数说明：
 ** value: 50, // 数值[必须]
 */
export const setNumberFloat = value => {
  const toNum = Number(value);
  if (isNaN(toNum)) return 0;
  return parseFloat(toNum.toFixed(4));
};

/* 【日期显示处理】
 ** 使用说明：将日期格式化，如果没有就显示none配置的值
 ** 参数说明：
 ** value: '2022-01-01 23:54:55', // 日期值[必须]
 ** format: 'YY/MM/DD', // 格式化规则[非必须]
 ** none: '--' // 无值时显示什么[非必须，默认'--']
 */
export const formatShowDate = (value, format = 'YY/MM/DD', none = '--') => {
  if (!value) return none;
  let showDate = value.toString();
  if (showDate.length === 10) {
    showDate = showDate.includes('-') ? new Date(showDate) : new Date(showDate * 1000);
  } else if (showDate.length === 13) {
    showDate = new Date(parseInt(showDate));
  } else {
    showDate = new Date(showDate);
  }
  showDate = moment(showDate).format(format);
  if (showDate.toLowerCase() === 'invalid date') {
    showDate = none;
  }
  return showDate;
};

/* 【判断功能、按钮权限】
 ** 使用说明：通过路由、功能code判断是否具有权限，跟跨领域common中ic-utils.js里全局指令v-auth的判断一致
 ** 参数说明：
 ** authObject: {
 **   authRoute: String, // 权限路由[非必须，不传视为有权限]
 **   authCode: String, // 功能、按钮的编码[非必须，不传视为有权限]
 ** }
 */
export const checkAuth = (authObject = {}) => {
  const { authRoute, authCode } = authObject;
  if (!authRoute || !authCode) return true;
  const auth = storage.get('profile') ? deepAssign(baseAuth, storage.get('profile').auth) : { ...baseAuth };
  let path = authRoute;
  if (/\?/.test(path)) {
    path = path.split('?')[0];
  }
  const urlArr = path.split('/').filter(item => item);
  return !!(getIn(urlArr, auth) && getIn(urlArr, auth)[authCode]);
};

/* 【详情页底部页签，判断当前选中是不是固定的那4个tab】
 ** 参数说明：
 ** nowTab: String // 当前选中的页签id[必须]
 ** fixedTabNav: Array // 页签数组[必须]
 */
export const checkoutIsFixedTab = (nowTab = '', fixedTabNav = []) => {
  return fixedTabNav.findIndex(item => item.id === nowTab) > -1;
};

/* 【在对象数组中，根据已有的一对键和值，找到目标key对应的value值】
 ** 参数说明：
 ** {
 **   keyName: String | Number, // 已知的键名[非必须，默认为id]
 **   keyValue: String | Number, // 已知键对应的值[必须]
 **   targetKey: String | Number, // 目标的键名[非必须，默认为name]
 **   list: Array, // 对象数组[必须，对象数组]
 **   none: String | Number // 没找到时指定返回什么[非必须，不传则没找到时返回'']
 ** }
 ** 拓展说明：如果觉得麻烦，直接用下面语义化的findNameById和findLabelByValue也行
 */
export const findValueByKey = ({ keyName = 'id', keyValue, targetKey = 'name', list = [], none = '' }) => {
  if (typeof keyValue === 'undefined') return '';
  const targetObj = list.find(item => item[keyName] === keyValue);
  if (targetObj) return targetObj[targetKey] || '';
  return none;
};
export const findNameById = (id, list, none = '--') => findValueByKey({ keyName: 'id', keyValue: id, targetKey: 'name', list, none });
export const findLabelByValue = (value, list, none = '--') => findValueByKey({ keyName: 'value', keyValue: value, targetKey: 'label', list, none });

/* 【根据收/支类型，给金额前面加+-符号】
 ** 参数说明：
 ** money: Number, // [必须]金额值
 ** isReceipt: Boolead, // [必须]是收入吗
 ** currencyId: String, // [非必须]币种id
 */
export const setMoneyPrefix = (money = 0, isReceipt = true, currencyId = '8') => {
  const amountValue = icFilters.formatMoney(money, currencyId);
  if (amountValue === '--' || amountValue === '0') return '--';
  const symbol = isReceipt ? '+' : '-';
  return `${symbol} ${amountValue}`;
};
